import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './BookingCalendar.css';
import { registerLocale } from "react-datepicker";
import { es } from 'date-fns/locale';
import { eachDayOfInterval, startOfMonth, endOfMonth, isSameDay, isBefore } from 'date-fns';
import { Turn } from '../../models/types';
import { parse } from 'date-fns';
import { getEarliestBookableDate, isDateBookable } from '../../utils/bookableDate';

registerLocale('es', es);

interface BookingCalendarProps {
    showThreeSlots: Boolean;
    availableTurns: Turn[];
    bankHolidays: Date[];
    selectedDate: Date | null;
    dateShown: Date;  
    onDateSelect: (date: Date) => void;
    onDateChange: (date: Date) => void;
    onMonthChange: (date: Date) => void;
}

const BookingCalendar: React.FC<BookingCalendarProps> = ({ showThreeSlots, availableTurns, bankHolidays, selectedDate, dateShown, onDateSelect, onDateChange, onMonthChange }) => {
    const [nonAvailableDates, setNonAvailableDates] = useState<Date[]>([]);
    const [pastDates, setPastDates] = useState<Date[]>([]);

    useEffect(() => {
        const monthStart = startOfMonth(dateShown);
        const monthEnd = endOfMonth(dateShown);
        const allDaysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
        const earliestBookableDate = getEarliestBookableDate();

        const nonAvailable = allDaysInMonth.filter(day =>
            !availableTurns.some(turn => isSameDay(parse(turn.booking_date, 'yyyy-MM-dd', new Date()), day))
        );

        const past = allDaysInMonth.filter(day => isBefore(day, earliestBookableDate));

        setNonAvailableDates(nonAvailable);
        setPastDates(past);
    }, [availableTurns, bankHolidays, selectedDate, dateShown]);

    useEffect(() => {
        const handleDayClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (target.classList.contains('react-datepicker__day')) {
                const dateString = target.getAttribute('aria-label');
                if (dateString) {
                    const dateMatch = dateString.match(/(\d{1,2}) de (\w+) de (\d{4})/);
                    if (dateMatch) {
                        const day = parseInt(dateMatch[1], 10);
                        const monthName = dateMatch[2];
                        const year = parseInt(dateMatch[3], 10);

                        const monthMap: { [key: string]: number } = {
                            'enero': 0,
                            'febrero': 1,
                            'marzo': 2,
                            'abril': 3,
                            'mayo': 4,
                            'junio': 5,
                            'julio': 6,
                            'agosto': 7,
                            'septiembre': 8,
                            'octubre': 9,
                            'noviembre': 10,
                            'diciembre': 11
                        };

                        const month = monthMap[monthName.toLowerCase()];
                        if (month !== undefined) {
                            const clickedDate = new Date(year, month, day);
                            if (!isNaN(clickedDate.getTime()) && isDateBookable(clickedDate)) {
                                onDateSelect(clickedDate);
                                onDateChange(clickedDate);
                            } else if (isNaN(clickedDate.getTime())) {
                                console.error('Invalid date:', clickedDate);
                            }
                        } else {
                            console.error('Invalid month name:', monthName);
                        }
                    } else {
                        console.error('Date string does not match expected format:', dateString);
                    }
                }
            }
        };

        document.addEventListener('click', handleDayClick);

        return () => {
            document.removeEventListener('click', handleDayClick);
        };
    }, [onDateSelect, onDateChange]);

    const getSlotAvailabilityClass = (date: Date) => {
        const earliestBookableDate = getEarliestBookableDate();
        const isMonthShown = date.getMonth() === (dateShown ? dateShown.getMonth() : earliestBookableDate.getMonth());

        if (!isMonthShown) {
            return 'react-datepicker__day--outside-month';
        }

        if (isBefore(date, earliestBookableDate)) {
            return 'react-datepicker__day--disabled-past';
        }

        if (selectedDate && isSameDay(date, selectedDate)) {
            return 'react-datepicker__day--selected';
        }

        const slot1Available = availableTurns.some(turn => isSameDay(parse(turn.booking_date, 'yyyy-MM-dd', new Date()), date) && turn.slot === 1);
        const slot2Available = availableTurns.some(turn => isSameDay(parse(turn.booking_date, 'yyyy-MM-dd', new Date()), date) && turn.slot === 2);
        const slot3Available = availableTurns.some(turn => isSameDay(parse(turn.booking_date, 'yyyy-MM-dd', new Date()), date) && turn.slot === 3);

        if (showThreeSlots) {
            if (slot1Available && slot2Available && slot3Available) {
                return 'slot-all-available';
            } else if (slot1Available && !slot2Available && !slot3Available) {
                return 'slot1-available';
            } else if (!slot1Available && slot2Available && !slot3Available) {
                return 'slot2-available';
            } else if (!slot1Available && !slot2Available && slot3Available) {
                return 'slot3-available';
            } else if (slot1Available && slot2Available && !slot3Available) {
                return 'slot1-2-available';
            } else if (slot1Available && !slot2Available && slot3Available) {
                return 'slot1-3-available';
            } else if (!slot1Available && slot2Available && slot3Available) {
                return 'slot2-3-available';
            } else {
                return 'slot-none-available';
            }
        } else {
            if (slot1Available && slot2Available) {
                return 'slot-all-available';
            } else if (slot1Available && !slot2Available) {
                return 'slot1-available_2slots';
            } else if (!slot1Available && slot2Available) {
                return 'slot2-available_2slots';
            } else {
                return 'slot-none-available';
            }
        }
    };

    return (
        <div className="booking-container">
            <div className="calendar-container">
                <DatePicker
                    selected={selectedDate}
                    minDate={getEarliestBookableDate()}
                    filterDate={isDateBookable}
                    onChange={(date: Date) => {
                        if (!isDateBookable(date)) {
                            return;
                        }
                        onDateSelect(date);
                        onDateChange(date);
                    }}
                    onMonthChange={(date: Date) => {
                        onMonthChange(date);
                    }}
                    highlightDates={[
                        {"react-datepicker__day--highlighted-custom-1": availableTurns.map(turn => parse(turn.booking_date, 'yyyy-MM-dd', new Date()))},
                        {"react-datepicker__day--not-available": nonAvailableDates},
                        {"react-datepicker__day--disabled-past": pastDates},
                        {"react-datepicker__day--bank-holiday": bankHolidays},
                        ...(selectedDate ? [{"react-datepicker__day--selected": [selectedDate]}] : [])
                    ]}
                    dayClassName={(date) => getSlotAvailabilityClass(date)}
                    inline
                    locale="es"
                    calendarStartDay={1}
                />
            </div>
        </div>
    );
};

export default BookingCalendar;
